import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { format, getTime } from 'date-fns';
import { get as _get, has as _has, some as _some } from 'lodash';

import { DriverDefaults, FnolModel } from '@amfam/claim/dynamic-fnol/data-access';
import { ClaimUtilService, PrettyClaimNum } from '@amfam/claim/shared/utility';
import { RiskModel } from '@amfam/policy/models';
import { ClaimsAnalytics } from '@amfam/shared/analytics';
import { ResumeDraftClaimModel } from '@amfam/shared/models';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DsModalService } from '@amfam/ui-kit';

@Component({
  selector: 'ds-claim-list',
  templateUrl: './claim-list.component.html',
  styleUrls: ['./claim-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ClaimListComponent implements OnInit, OnDestroy {
  @Input() claimList = [];
  @Input() risks: Array<RiskModel> = new Array<RiskModel>();
  @Input() isDraft: boolean;
  @Input() sidebarPresent = false;
  @Input() hideDelete = false;
  @Input() riskDescriptionForFailedClaim = '';
  @Input() isValidRiskForFailedClaim = false;
  @Input() lossDateForFailedClaim = '';
  @Output() resumeDraftClaimEvent = new EventEmitter<ResumeDraftClaimModel>();
  @Output() deleteDraftClaimEvent = new EventEmitter<string>();
  @Output() routeToClaimEvent = new EventEmitter<string>();

  pageLimit = 5;
  footerHeight = '';
  columns = [{}];
  rows = [{}];
  cssClasses = 'material noshadow';
  modalId = 'deleteDraftClaim';
  selectedDraftClaim;
  claimColumnName = 'Claim';
  showViewDetailBtn = true;
  resumeDraftAnalytic = ClaimsAnalytics.clickClaimResumeDraft;

  @ViewChild('headerCellTpl', { static: true }) headerCellTpl: TemplateRef<any>;
  @ViewChild('claimNameCellTpl', { static: true }) claimNameCellTpl: TemplateRef<any>;
  @ViewChild('submitDateCellTpl', { static: true }) submitDateCellTpl: TemplateRef<any>;
  @ViewChild('statusCellTpl', { static: true }) statusCellTpl: TemplateRef<any>;
  @ViewChild('actionCellTpl', { static: true }) actionCellTpl: TemplateRef<any>;
  @ViewChild('deleteModalTpl', { static: true }) deleteModalTpl: TemplateRef<any>;

  constructor(
    private claimUtilService: ClaimUtilService,
    private modalService: DsModalService,
    public roleService: ImpersonateRolesService,
    private feature: FeatureFlagService,
    private copyService: CopyService
  ) {}

  ngOnInit() {
    this.claimList = this.claimList.sort((obj1, obj2) => {
      return getTime(obj2.openDate) - getTime(obj1.openDate);
    });
    this.buildRows(this.claimList);
    this.buildColumns();
  }

  ngOnDestroy() {
    // Close this so the modal isn't trying to open the next time this component renders
    this.modalService.close(this.modalId);
    this.selectedDraftClaim = {};
  }

  private buildRows(claims: any[]) {
    this.rows = [];
    if (claims && claims.length) {
      claims.forEach(claim => {
        this.rows.push({
          claimName: this.isDraft
            ? 'Saved Draft'
            : 'Claim ' + new PrettyClaimNum().transform(claim.claimNumber),
          riskDescription: this.riskDescriptionForFailedClaim.length
            ? this.riskDescriptionForFailedClaim
            : this.determineRiskDescription(claim),
          submitDate: format(claim.openDate.split('T')[0], 'MM/DD/YYYY'),
          incidentDate: this.lossDateForFailedClaim.length
            ? format(this.lossDateForFailedClaim.split('T')[0], 'MM/DD/YYYY')
            : format(claim.lossDate.split('T')[0], 'MM/DD/YYYY'),
          claimSteps: claim.useProgressTrackingIndicator
            ? this.claimUtilService.buildIndicatorStatus(claim)
            : {},
          useProgressTrackingIndicator: claim.useProgressTrackingIndicator ? true : false,
          claimNumber: claim.claimNumber,
          claimObj: claim,
          isDraft: this.isDraft ? this.isDraft : false,
          isValidRisk: this.isValidRiskForFailedClaim
            ? this.isValidRiskForFailedClaim
            : this.determineRiskValidity(claim),
          status: this.determineStatusString(claim),
          showViewDetailBtn: _get(claim, 'detailLoaded', true)
        });
      });
    }

    // Enable pagination if we have enough autoPay Rules.
    if (this.rows && this.rows.length > this.pageLimit) {
      this.footerHeight = 'auto';
    } else {
      this.footerHeight = '';
    }
  }

  private determineRiskDescription(claim: any): string {
    let riskDescription = '';
    // Make sure we get autoClaimDetails or autoClaimDetail as part of claims/partyId?=fnols.
    if (_get(claim, 'vehicleNotOnPolicy', false)) {
      riskDescription = this.copyService.getCopy('claims.fnol.vehicleNotOnPolicy');
    } else if (
      _has(claim, 'autoClaimDetail.vehicles') &&
      claim.autoClaimDetail.vehicles.length > 0 &&
      claim.autoClaimDetail.vehicles[0].year &&
      claim.autoClaimDetail.vehicles[0].make &&
      claim.autoClaimDetail.vehicles[0].model
    ) {
      riskDescription = !!claim.autoClaimDetail.vehicles[0].vin
        ? `${claim.autoClaimDetail.vehicles[0].year} ${claim.autoClaimDetail.vehicles[0].make} ${claim.autoClaimDetail.vehicles[0].model}`
        : this.copyService.getCopy('claims.fnol.vehicleNotOnPolicy');
    } else if (
      _has(claim, 'autoClaimDetails') &&
      claim.autoClaimDetails.length > 0 &&
      claim.autoClaimDetails[0].year &&
      claim.autoClaimDetails[0].make &&
      claim.autoClaimDetails[0].model
    ) {
      riskDescription = !!claim.autoClaimDetails[0].vin
        ? `${claim.autoClaimDetails[0].year} ${claim.autoClaimDetails[0].make} ${claim.autoClaimDetails[0].model}`
        : this.copyService.getCopy('claims.fnol.vehicleNotOnPolicy');
    } else if (_has(claim, 'risk')) {
      riskDescription =
        _get(claim, 'risk.vehicleTypeName') === DriverDefaults.VEHICLE_NOT_ON_POLICY
          ? this.copyService.getCopy('claims.fnol.vehicleNotOnPolicy')
          : _get(claim, 'risk.description');
      riskDescription = `${riskDescription}`;
    } else if (
      _get(claim, 'autoClaimDetail.vehicles.length') === 0 ||
      _get(claim, 'autoClaimDetails.length') === 0
    ) {
      riskDescription =
        _get(claim, 'lossLocationAddress.addr1') || _get(claim, 'lossLocationAddress[0].addr1');
      riskDescription = `${riskDescription}`; // Risk description for property
    }
    return riskDescription;
  }

  private determineRiskValidity(claim: any): boolean {
    let vin = '';
    // If we have autoClaimDetail or autoClaimDetails, we can allow a claim with a vin,
    // or one without a vin if autoFnol is enabled (for a vehicle not on policy)
    if (
      _has(claim, 'autoClaimDetail.vehicles') &&
      claim.autoClaimDetail.vehicles.length > 0 &&
      (this.feature.isEnabled('auto_fnol') || claim.autoClaimDetail.vehicles[0].vin)
    ) {
      return true;
    } else if (
      _has(claim, 'autoClaimDetails') &&
      claim.autoClaimDetails.length > 0 &&
      (this.feature.isEnabled('auto_fnol') || claim.autoClaimDetails[0].vin)
    ) {
      return true;
    } else if (_has(claim, 'risk')) {
      vin = claim.risk.vin;
    }
    if (!!vin) {
      return _some(this.risks, { vin: vin });
    } else {
      return (
        !!_get(claim, 'lossLocationAddress.addr1') ||
        !!_get(claim, 'lossLocationAddress[0].addr1') ||
        !!_get(claim, 'risk.address.addressLine1')
      ); // Validate property risk
    }
  }

  private determineStatusString(claim: any) {
    let status;
    if (this.isDraft) {
      status = 'Draft';
    } else if (claim.useProgressTrackingIndicator === true) {
      status = claim.claimProgress;
    } else {
      switch (claim.claimStatus) {
        case 'OPEN':
        case 'IN_PROGRESS':
        case 'OPEN_REPORTED':
        case 'AUTOPAY_PENDING':
        case 'AUTOPAY_REVIEW':
        case 'AWAITING_SETUP':
          status = 'Open';
          break;
        case 'ARCHIVED':
        case 'CLOSED':
        case 'VOID':
        case 'CLOSED_AUTO_PAY':
        case 'CLOSED_ERROR':
        case 'REOPEN':
          status = 'Closed';
          break;
      }
    }

    return status;
  }

  private buildColumns() {
    this.columns = [
      {
        cellTemplate: this.claimNameCellTpl,
        headerTemplate: this.headerCellTpl,
        isHeaderOnMobile: true,
        name: this.isDraft ? 'Drafts' : 'Claim',
        prop: 'claimName',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 220
      },
      {
        cellTemplate: this.submitDateCellTpl,
        headerTemplate: this.headerCellTpl,
        name: 'Submit Date',
        prop: 'submitDate',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 160
      },
      {
        cellTemplate: this.statusCellTpl,
        headerTemplate: this.headerCellTpl,
        name: 'Status',
        prop: '',
        draggable: false,
        resizeable: false,
        canAutoResize: false,
        width: 120
      },
      {
        cellTemplate: this.actionCellTpl,
        headerTemplate: this.headerCellTpl,
        name: '',
        prop: '',
        draggable: false
      }
    ];
  }

  public openDeleteDraftClaimModal(draftClaim) {
    if (draftClaim && draftClaim.claimObj) {
      this.selectedDraftClaim = draftClaim;
      window.setTimeout(() => {
        this.modalService.open(this.modalId);
      }, 0);
    }
  }

  public clearSelectedDraftClaim() {
    this.selectedDraftClaim = {};
  }

  public closeDeleteDraftClaimModal() {
    this.clearSelectedDraftClaim();
    this.modalService.close(this.modalId);
  }

  public deleteDraftClaim() {
    if (this.selectedDraftClaim) {
      this.deleteDraftClaimEvent.emit(this.selectedDraftClaim.claimNumber);
      this.closeDeleteDraftClaimModal();
    }
  }

  public resumeDraftClaim(claim: FnolModel) {
    const resumeDraftClaim: ResumeDraftClaimModel = {
      claimNumber: claim.claimNumber,
      risk: this.getCurrentRisk(claim),
      isNewDigitalClaim: _get(claim, 'claimObj.isNewDigitalClaim', false),
      claimStatus: _get(claim, 'claimObj.claimStatus', 'IN PROGRESS') // Default to IN PROGRESS for draft claim where claimStatus value is not available.
    };
    this.resumeDraftClaimEvent.emit(resumeDraftClaim);
  }

  private getCurrentRisk(claim): RiskModel {
    let vin = '';
    if (
      _has(claim, 'claimObj.autoClaimDetail.vehicles') &&
      claim.claimObj.autoClaimDetail.vehicles.length > 0 &&
      claim.claimObj.autoClaimDetail.vehicles[0].vin
    ) {
      vin = claim.claimObj.autoClaimDetail.vehicles[0].vin;
    } else if (
      _has(claim, 'claimObj.autoClaimDetails') &&
      claim.claimObj.autoClaimDetails.length > 0 &&
      claim.claimObj.autoClaimDetails[0].vin
    ) {
      vin = claim.claimObj.autoClaimDetails[0].vin;
    } else if (_has(claim, 'claimObj.risk')) {
      vin = claim.claimObj.risk.vin;
    }

    if (!!vin) {
      return this.risks.find(risk => risk.vin === vin);
    } else {
      return this.risks.find(
        risk => _get(risk, 'policyNumber') === _get(claim, 'claimObj.policyNumber')
      );
    }
  }
}
